<template>
  <div class="guc-root" :class="rootClasses">

    <!-- ── FAB (solo en modo standalone) ── -->
    <transition name="guc-fab-pop">
      <button
        v-if="!isOpen && !controlled"
        class="guc-fab"
        @click="openChat"
        aria-label="Abrir chat"
      >
        <v-icon color="white" size="21">mdi-forum-outline</v-icon>
        <span v-if="hasUnread" class="guc-fab__dot" />
      </button>
    </transition>

    <!-- ── Ventana ── -->
    <transition name="guc-win-slide">
      <div
        v-if="isOpen"
        class="guc-window"
        :class="{ 'guc-window--fullscreen': isFullscreen }"
        :style="{ transformOrigin: anchor === 'left' ? 'bottom left' : 'bottom right' }"
        role="dialog"
        aria-label="Chat"
      >
        <!-- Header -->
        <div class="guc-header">
          <div class="d-flex align-center" style="gap: 10px; min-width: 0;">
            <v-btn v-if="selectedUser" icon x-small @click="backToList" class="guc-icon-btn">
              <v-icon size="16" color="rgba(255,255,255,.75)">mdi-arrow-left</v-icon>
            </v-btn>
            <div class="guc-avatar">
              <v-icon color="white" size="16">{{ selectedUser ? 'mdi-account' : 'mdi-forum-outline' }}</v-icon>
            </div>
            <div style="min-width: 0;">
              <p class="guc-header__title mb-0">{{ selectedUserData ? selectedUserData.name : 'Chat' }}</p>
              <p class="guc-header__sub mb-0">
                <template v-if="selectedUserData">
                  <span class="guc-status-dot" :class="selectedUserData.online ? 'guc-status-dot--on' : 'guc-status-dot--off'" />
                  {{ selectedUserData.online ? 'En línea' : 'Desconectado' }}
                </template>
                <template v-else>Selecciona con quién hablar</template>
              </p>
            </div>
          </div>
          <div class="d-flex align-center" style="gap: 4px;">
            <v-btn icon x-small @click="toggleFullscreen" :title="isFullscreen ? 'Reducir' : 'Pantalla completa'" class="guc-icon-btn">
              <v-icon size="15" color="rgba(255,255,255,.65)">
                {{ isFullscreen ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}
              </v-icon>
            </v-btn>
            <v-btn icon x-small @click="closeChat" title="Cerrar" class="guc-icon-btn">
              <v-icon size="15" color="rgba(255,255,255,.65)">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- ── Listado de Salas y Contactos ── -->
        <div v-if="!selectedUser" class="guc-user-list">
          <div v-if="roomsLoading" class="guc-empty">Cargando salas…</div>

          <div v-else>
            <div v-if="chatRooms.length">
              <div class="guc-section-title" style="padding:8px 12px;font-size:12px;color:#6b7280">Salas</div>
              <button
                v-for="room in chatRooms"
                :key="room.id"
                class="guc-user-row"
                @click="selectRoom(room)"
              >
                <div class="guc-user-avatar">
                  <v-icon size="16" color="#2D4B95">mdi-forum</v-icon>
                  <span class="guc-status-dot" :class="room.online ? 'guc-status-dot--on' : 'guc-status-dot--off'" />
                </div>
                <div class="guc-user-info">
                  <span class="guc-user-name">{{ room.other_user_data ? room.other_user_data.name : (room.name || 'Chat') }}</span>
                  <span class="guc-user-status">{{ room.last_message ? room.last_message.message : (room.other_user_data ? (room.other_user_data.online ? 'En línea' : 'Desconectado') : 'Sin mensajes') }}</span>
                </div>
              </button>
            </div>

            <div v-if="contacts.length">
              <div class="guc-section-title" style="padding:8px 12px;font-size:12px;color:#6b7280">Contactos</div>
              <button
                v-for="user in contacts"
                :key="user.id"
                class="guc-user-row"
                @click="selectUser(user)"
              >
                <div class="guc-user-avatar">
                  <v-icon size="16" color="#2D4B95">mdi-account</v-icon>
                  <span class="guc-status-dot" :class="user.online ? 'guc-status-dot--on' : 'guc-status-dot--off'" />
                </div>
                <div class="guc-user-info">
                  <span class="guc-user-name">{{ user.name }}</span>
                  <span class="guc-user-status">{{ user.online ? 'En línea' : 'Desconectado' }}</span>
                </div>
              </button>
            </div>

            <div v-if="!chatRooms.length && !contacts.length" class="guc-empty">No hay usuarios disponibles.</div>
          </div>
        </div>

        <!-- ── Conversación ── -->
        <template v-else>
          <div class="guc-messages" ref="msgBox">
            <div v-if="isLoadingMessages" class="guc-empty">Cargando mensajes…</div>
            <div v-else-if="!messages.length" class="guc-empty">Aún no hay mensajes. Escribe el primero.</div>
            <div
              v-for="(msg, i) in messages"
              :key="msg.id || msg._localId || i"
              class="guc-msg"
              :class="isMine(msg) ? 'guc-msg--me' : 'guc-msg--them'"
            >
              <div class="guc-msg__bubble">
                {{ msg.message }}
                <span v-if="msg._status === 'sending'" class="guc-msg__sending"> · Enviando…</span>
                <span v-if="msg._status === 'error'" class="guc-msg__error"> · Error</span>
              </div>
              <span class="guc-msg__time">{{ formatTime(msg.created_at) }}</span>
            </div>
          </div>

          <div class="guc-input-area">
            <textarea
              ref="inputEl"
              v-model="draft"
              class="guc-input"
              placeholder="Escribe un mensaje…"
              rows="1"
              :disabled="isSending"
              @keydown.enter.exact.prevent="sendMessage"
              @input="resizeInput"
              @click.stop
            />
            <button
              class="guc-send"
              :disabled="isSending || !draft.trim()"
              @click.stop="sendMessage"
              aria-label="Enviar"
            >
              <v-icon size="17" :color="(isSending || !draft.trim()) ? '#bbb' : 'white'">mdi-send</v-icon>
            </button>
          </div>
        </template>

      </div>
    </transition>
  </div>
</template>

<script>
import { ref, onValue, off, getDatabase } from 'firebase/database'
import EventBus from '@/helpers/event_bus'
import { getProjectChatRooms, getRoomMessages, sendRoomMessage } from '@/helpers/api/chat'

export default {
  name: 'UsersChat',

  props: {
    fixed: { type: Boolean, default: false },
    // Modo controlado: la apertura/cierre la maneja un componente padre
    // (ej. GlobalFloatingButtons) a través de la prop `open`.
    controlled: { type: Boolean, default: false },
    open: { type: Boolean, default: false },
    // Lado hacia el que se ancla/expande la ventana ('left' | 'right')
    anchor: { type: String, default: 'right' },
    // Sala/usuario a abrir directamente (ej. desde una notificación vía EventBus)
    // Forma esperada: { roomId, userId }
    targetRoom: { type: Object, default: null }
  },

  data: () => ({
    isOpen: false,
    isFullscreen: false,
    hasUnread: false,
    _lastTargetKey: null,

    selectedUser: null,
    messages: [],
    draft: '',
    isLoadingMessages: false,
    isSending: false,

    // Salas y contactos
    chatRooms: [],
    roomsLoading: false,
    currentChatRoom: null,

    // Firebase presencia
    presenceMap: {},
    presenceOldMap: {},
    presenceNewMap: {},
    
    projectRefs: { old: null, new: null },
  }),

  computed: {
    db() {
      return getDatabase()
    },
    rootClasses() {
      const classes = []
      if (this.fixed) classes.push('guc-fixed')
      if (this.controlled) classes.push('guc-controlled-' + this.anchor)
      return classes
    },

    selectedProject() {
      return this.$store.projectsStore.state.selectedProject
    },

    // TODO: ajustar al getter real del usuario autenticado
    userInfo() {
      return this.$store.userStore.state.userInfo
    },

    myId() {
      return this.userInfo ? this.userInfo.id : null
    },

    rawUsers() {
      return this.$store.usersStore.state.usersList || []
    },

    // Usuarios + estado de conexión (firebase), conectados primero
    mergedUsers() {
      return this.rawUsers
        .filter(u => u.id !== this.myId)
        .map(u => ({
          ...u,
          online: !!(this.presenceMap[u.id] && this.presenceMap[u.id].status === 'online')
        }))
        .sort((a, b) => {
          if (a.online !== b.online) return a.online ? -1 : 1
          return (a.name || '').localeCompare(b.name || '')
        })
    },

    contacts() {
      const inRooms = new Set(this.chatRooms.flatMap(r => r.other_user_id ? [r.other_user_id] : []))
      return this.rawUsers
        .filter(u => u.id !== this.myId && !inRooms.has(u.id))
        .map(u => ({ ...u, online: !!(this.presenceMap[u.id] && this.presenceMap[u.id].status === 'online') }))
        .sort((a, b) => {
          if (a.online !== b.online) return a.online ? -1 : 1
          return (a.name || '').localeCompare(b.name || '')
        })
    },

    selectedUserData() {
      if (!this.selectedUser) return null
      let user = this.mergedUsers.find(u => u.id === this.selectedUser) || null
      if (!user && this.currentChatRoom && this.currentChatRoom.other_user_data) {
        user = this.currentChatRoom.other_user_data
      }
      return user
    },
  },

  watch: {
    isOpen(val) {
      if (val) {
        this.$nextTick(() => this.scrollBottom())
      }
    },

    // Modo controlado: un padre (GlobalFloatingButtons) decide cuándo abrir/cerrar
    open(val) {
      if (!this.controlled) return
      if (val) this.openChat()
      else this.closeChat()
    },

    // Permite saltar a una sala/usuario específico incluso si el chat
    // ya estaba abierto (ej. llega otra notificación mientras se conversa).
    targetRoom(val) {
      if (val && this.isOpen) this.goToTarget(val)
    },

    selectedProject() {
      // Si cambia el proyecto activo, reiniciamos presencia/listado
      if (this.isOpen) {
        this.stopListenUsers()
        this.startListenUsers()
        this.loadChatRooms()
      }
    },

    presenceMap() {
      // actualizar flags online en las salas
      this.chatRooms.forEach(r => {
        const other = r.other_user_id
        r.online = !!(other && this.presenceMap[other] && this.presenceMap[other].status === 'online')
        if (!r.other_user_data && other) {
          r.other_user_data = this.rawUsers.find(u => u.id === other) || r.other_user_data
        }
      })
    }
  },

  mounted() {
    // Se recarga la conversación abierta cuando llega una notificación externa
    EventBus.$on('reloadChatRoom', this.reloadChatRoom)
  },

  beforeDestroy() {
    EventBus.$off('reloadChatRoom', this.reloadChatRoom)
    this.stopListenUsers()
  },

  methods: {
    // ── UI ──────────────────────────────────────────────────

    // Determina si un mensaje me pertenece. Los mensajes que llegan
    // recién enviados (optimistic UI) traen sender_id como Number (this.myId),
    // pero los que vienen desde la API pueden traer sender_id como String,
    // o incluso venir en otras llaves (user_id, sender.id). Por eso acá
    // normalizamos todo a String y cubrimos los formatos alternativos
    // antes de comparar.
    isMine(msg) {
      if (!msg || this.myId === null || this.myId === undefined) return false

      const candidates = [
        msg.sender_id,
        msg.user_id,
        msg.from_id,
        msg.sender && msg.sender.id,
        msg.user && msg.user.id
      ].filter(v => v !== undefined && v !== null)

      if (!candidates.length) return false

      return candidates.some(id => String(id) === String(this.myId))
    },

		reloadChatRoom(data) {
			if (!this.selectedProject || !data) return
			if (data.project_id !== this.selectedProject.id) return
			if (this.currentChatRoom && data.chat_room_id === this.currentChatRoom.id) {
				this.fetchMessages(this.selectedUser)
			}
		},

    async openChat() {
      this.isOpen = true
      this.hasUnread = false
      this.startListenUsers()
      await this.loadChatRooms()
      if (this.targetRoom) this.goToTarget(this.targetRoom)
    },

    // Resuelve { roomId, userId } a una sala abierta en la conversación.
    // Deduplicado por key para no reprocesar el mismo target dos veces
    // (puede llegar tanto desde el watcher como desde openChat()).
    async goToTarget(target) {
      if (!target) return
      const key = `${target.roomId || ''}_${target.userId || ''}`
      if (!key.replace('_', '')) return
      if (key === this._lastTargetKey) return
      this._lastTargetKey = key

      if (target.roomId) {
        let room = this.chatRooms.find(r => r.id === target.roomId)
        if (!room) {
          await this.loadChatRooms()
          room = this.chatRooms.find(r => r.id === target.roomId)
        }
        if (room) {
          this.selectRoom(room)
          return
        }
      }

      if (target.userId) {
        this.selectedUser = target.userId
        this.fetchMessages(target.userId)
      }
    },

    closeChat() {
      this.isOpen = false
      this.isFullscreen = false
      this.stopListenUsers()
      if (this.controlled) this.$emit('close')
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
      this.$nextTick(() => this.scrollBottom())
    },

    backToList() {
      this.selectedUser = null
      this.messages = []
      this.draft = ''
      this.currentChatRoom = null
    },

    selectUser(user) {
      this.selectedUser = user.id
      this.fetchMessages(user.id)
    },

    selectRoom(room) {
      // determine other participant id if available
      const otherId = this.getOtherUserIdFromRoom(room)
      if (otherId) {
        this.selectedUser = otherId
        this.currentChatRoom = room
        this.fetchMessages(otherId)
      } else {
        // fallback: try to load rooms again and then select
        this.loadChatRooms().then(() => {
          const r = this.chatRooms.find(rr => rr.id === room.id)
          const other = r ? this.getOtherUserIdFromRoom(r) : null
          if (other) {
            this.selectedUser = other
            this.currentChatRoom = r
            this.fetchMessages(other)
          } else {
            // last resort: pick first user that is not me
            const fallback = this.rawUsers.find(u => u.id !== this.myId)
            if (fallback) this.selectUser(fallback)
          }
        })
      }
    },

    resizeInput() {
      const el = this.$refs.inputEl
      if (!el) return
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 108) + 'px'
    },

    scrollBottom() {
      this.$nextTick(() => {
        const c = this.$refs.msgBox
        if (c) c.scrollTop = c.scrollHeight
      })
    },

    formatTime(value) {
      if (!value) return ''
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
    },

    // ── Presencia Firebase (gestionada por FirebaseController) ──

    startListenUsers() {
      if (!this.selectedProject || this.projectRefs.new || this.projectRefs.old) return
      this.projectRefs.old = ref(this.db, `${this.selectedProject.id}`)
      this.projectRefs.new = ref(this.db, `${this.selectedProject.id}/chats`)

      onValue(this.projectRefs.old, (snapshot) => {
        this.presenceOldMap = snapshot.val() || {}
        this.presenceMap = { ...this.presenceOldMap, ...this.presenceNewMap }
      })

      onValue(this.projectRefs.new, (snapshot) => {
        this.presenceNewMap = snapshot.val() || {}
        this.presenceMap = { ...this.presenceOldMap, ...this.presenceNewMap }
      })
    },

    stopListenUsers() {
      if (this.projectRefs.old) {
        try { off(this.projectRefs.old) } catch (e) { void e }
        this.projectRefs.old = null
      }
      if (this.projectRefs.new) {
        try { off(this.projectRefs.new) } catch (e) { void e }
        this.projectRefs.new = null
      }
      this.presenceMap = {}
      this.presenceOldMap = {}
      this.presenceNewMap = {}
    },

    // ── Mensajes (persistencia vía backend, no firebase) ────────

    reloadList() {
      if (this.selectedUser) this.fetchMessages(this.selectedUser)
    },

    async fetchMessages(userId) {
      this.isLoadingMessages = true
      try {
        const data = { user_id: userId, project_id: this.selectedProject?.id }
        const response = await getRoomMessages(data)
        const chat_room = response?.chat_room || null
        this.messages = (chat_room && chat_room.messages) ? chat_room.messages : []
        this.currentChatRoom = chat_room
        // Ensure the room is present in the chatRooms list
        if (chat_room) {
          const exists = this.chatRooms.find(r => r.id === chat_room.id)
          const otherId = this.getOtherUserIdFromRoom(chat_room) || userId
          const otherData = this.rawUsers.find(u => u.id === otherId) || null
          const roomEntry = {
            ...chat_room,
            other_user_id: otherId,
            other_user_data: otherData,
            online: !!(this.presenceMap[otherId] && this.presenceMap[otherId].status === 'online')
          }
          if (!exists) {
            this.chatRooms.unshift(roomEntry)
          } else {
            Object.assign(exists, roomEntry)
          }
          this.sortChatRooms()
        }
        this.scrollBottom()
      } catch {
        this.messages = []
      } finally {
        this.isLoadingMessages = false
      }
    },

    async sendMessage() {
      const text = this.draft.trim()
      if (!text || this.isSending || !this.selectedUser) return

      // prepare optimistic message
      const localId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2,8)}`
      const tempMsg = {
        _localId: localId,
        message: text,
        sender_id: this.myId,
        created_at: new Date().toISOString(),
        _status: 'sending'
      }

      // clear input
      this.draft = ''
      this.$nextTick(() => {
        const el = this.$refs.inputEl
        if (el) el.style.height = 'auto'
      })

      // optimistic UI update
      this.messages.push(tempMsg)
      const roomBefore = this.chatRooms.find(r => r.id === this.currentChatRoom?.id)
      if (roomBefore) {
        roomBefore.last_message = tempMsg
        this.sortChatRooms()
      }
      this.scrollBottom()

      this.isSending = true

      try {
        if (!this.currentChatRoom || !this.currentChatRoom.id) {
          await this.fetchMessages(this.selectedUser)
        }
        const payload = { chat_room_id: this.currentChatRoom.id, message: text }
        const response = await sendRoomMessage(payload)
        const serverMsg = response?.message || response?.chat_room?.last_message || {
          message: text,
          sender_id: this.myId,
          created_at: new Date().toISOString(),
        }

        // replace optimistic message with server message
        const idx = this.messages.findIndex(m => m._localId === localId)
        if (idx !== -1) {
          const finalMsg = { ...serverMsg, _status: 'sent' }
          if (!finalMsg.created_at) finalMsg.created_at = tempMsg.created_at
          this.$set(this.messages, idx, finalMsg)
        } else {
          this.messages.push(serverMsg)
        }

        // update room last_message and reorder
        const room = this.chatRooms.find(r => r.id === this.currentChatRoom.id)
        if (room) {
          room.last_message = serverMsg
          this.sortChatRooms()
        }
        this.scrollBottom()
      } catch (e) {
        // mark optimistic message as failed
        const idx = this.messages.findIndex(m => m._localId === localId)
        if (idx !== -1) {
          this.$set(this.messages, idx, { ...this.messages[idx], _status: 'error' })
        }
        console.error('Error sending message', e)
      } finally {
        this.isSending = false
      }
    },

    async loadChatRooms() {
      this.roomsLoading = true
      try {
        const response = await getProjectChatRooms({ project_id: this.selectedProject?.id })
        const rooms = response?.my_chat_rooms || []
        // Enhance rooms with other participant and online flags where possible
        this.chatRooms = rooms.map(r => {
          const otherId = this.getOtherUserIdFromRoom(r)
          const otherData = this.rawUsers.find(u => u.id === otherId) || null
          return {
            ...r,
            other_user_id: otherId,
            other_user_data: otherData,
            online: !!(otherId && this.presenceMap[otherId] && this.presenceMap[otherId].status === 'online')
          }
        })
        this.sortChatRooms()
      } catch (e) {
        this.chatRooms = []
      } finally {
        this.roomsLoading = false
      }
    },

    getOtherUserIdFromRoom(room) {
      if (!room) return null
      // Possible shapes: room.users => [{user:{id,...}}] or [{user_id:...}]
      if (Array.isArray(room.users) && room.users.length) {
        const rel = room.users.find(u => {
          const uid = u.user ? u.user.id : u.user_id
          return uid && uid !== this.myId
        })
        if (rel) return rel.user ? rel.user.id : rel.user_id
      }
      // if room.users.user exists nested as 'users.user'
      if (Array.isArray(room.users_user) && room.users_user.length) {
        const rel = room.users_user.find(u => u.id && u.id !== this.myId)
        if (rel) return rel.id
      }
      // fallback: use last_message sender if it is not me
      if (room.last_message && room.last_message.sender_id && room.last_message.sender_id !== this.myId) {
        return room.last_message.sender_id
      }
      return null
    },

    sortChatRooms() {
      this.chatRooms.sort((a, b) => {
        const aTime = a.last_message ? new Date(a.last_message.created_at).getTime() : 0
        const bTime = b.last_message ? new Date(b.last_message.created_at).getTime() : 0
        if (aTime !== bTime) return bTime - aTime
        if ((a.online ? 1 : 0) !== (b.online ? 1 : 0)) return a.online ? -1 : 1
        const aName = (a.other_user_data?.name || a.name || '').toLowerCase()
        const bName = (b.other_user_data?.name || b.name || '').toLowerCase()
        return aName.localeCompare(bName)
      })
    },
  },
}
</script>

<style scoped>
.guc-root {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  pointer-events: auto !important;
}

.guc-fixed {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9998;
}

.guc-controlled-left {
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 9998;
}
.guc-controlled-right {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9998;
}

/* ── FAB ── */
.guc-fab {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #1C3A7A;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 18px rgba(28, 58, 122, .45);
  transition: background .18s, transform .15s, box-shadow .18s;
  position: relative;
}
.guc-fab:hover {
  background: #2D4B95;
  transform: scale(1.06);
  box-shadow: 0 6px 24px rgba(28, 58, 122, .6);
}
.guc-fab__dot {
  position: absolute;
  top: 8px; right: 8px;
  width: 9px; height: 9px;
  border-radius: 50%;
  background: #ef4444;
  border: 2px solid white;
}

/* ── Ventana ── */
.guc-window {
  width: 370px;
  height: 510px;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 20px 60px rgba(0,0,0,.18), 0 4px 16px rgba(0,0,0,.1);
  border: 1px solid #E0E6F5;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  pointer-events: auto !important;
}

.guc-window--fullscreen {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  width: 100vw !important;
  height: 100vh !important;
  border-radius: 0 !important;
  z-index: 9999;
}

/* ── Header ── */
.guc-header {
  background: #1C3A7A;
  padding: 13px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}
.guc-avatar {
  width: 30px; height: 30px;
  border-radius: 50%;
  background: rgba(255,255,255,.15);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.guc-header__title {
  font-size: 13px; font-weight: 600; color: #fff; line-height: 1.2;
  max-width: 190px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.guc-header__sub {
  font-size: 11px; color: rgba(255,255,255,.6); line-height: 1.3;
  display: flex; align-items: center; gap: 5px;
}
.guc-icon-btn {
  background: rgba(255,255,255,.08) !important;
  transition: background .15s !important;
}
.guc-icon-btn:hover { background: rgba(255,255,255,.2) !important; }

/* ── Estado (dot) ── */
.guc-status-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
.guc-status-dot--on { background: #2ecc71; }
.guc-status-dot--off { background: #c3c9d6; }

/* ── Listado de usuarios ── */
.guc-user-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.guc-user-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 8px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
  transition: background .12s;
}
.guc-user-row:hover { background: #F3F6FB; }

.guc-user-avatar {
  position: relative;
  width: 32px; height: 32px;
  border-radius: 50%;
  background: #EEF2FB;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.guc-user-avatar .guc-status-dot {
  position: absolute;
  bottom: 0; right: 0;
  border: 2px solid #fff;
}

.guc-user-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.guc-msg__sending { font-size: 10px; color: #9aa1b5; margin-left: 6px; }
.guc-msg__error { font-size: 10px; color: #ef4444; font-weight: 600; margin-left: 6px; }
.guc-user-name {
  font-size: 12.5px; font-weight: 600; color: #2c3352;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.guc-user-status {
  font-size: 11px; color: #9aa1b5;
}

.guc-empty {
  padding: 24px 14px;
  text-align: center;
  font-size: 12px;
  color: #aab0c0;
}

/* ── Mensajes ── */
.guc-messages {
  flex: 1;
  overflow-y: auto;
  padding: 14px 14px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  scrollbar-width: thin;
  scrollbar-color: #E0E6F5 transparent;
}
.guc-messages::-webkit-scrollbar { width: 4px; }
.guc-messages::-webkit-scrollbar-thumb { background: #E0E6F5; border-radius: 2px; }

.guc-msg {
  display: flex; flex-direction: column;
  max-width: 86%;
}
.guc-msg--me  { align-self: flex-end;  align-items: flex-end; }
.guc-msg--them { align-self: flex-start; align-items: flex-start; }

.guc-msg__bubble {
  padding: 8px 12px; border-radius: 10px;
  font-size: 12.5px; line-height: 1.6; word-break: break-word;
}
.guc-msg--me .guc-msg__bubble {
  background: #2D4B95; color: #fff; border-bottom-right-radius: 3px;
}
.guc-msg--them .guc-msg__bubble {
  background: #F3F6FB; color: #2c3352; border-bottom-left-radius: 3px;
}
.guc-msg__time { font-size: 10px; color: #aab0c0; margin-top: 3px; padding: 0 2px; }

/* ── Input ── */
.guc-input-area {
  display: flex; align-items: flex-end; gap: 8px;
  padding: 10px 12px; border-top: 1px solid #F0F0F0;
  background: #fafbff; flex-shrink: 0;
}
.guc-input {
  flex: 1; resize: none;
  border: 1px solid #E0E6F5; border-radius: 8px;
  background: #fff; color: #2c3352;
  font-size: 12.5px; font-family: inherit;
  padding: 8px 11px; line-height: 1.5; outline: none;
  min-height: 34px; max-height: 108px; overflow-y: auto;
  transition: border-color .18s;
}
.guc-input::placeholder { color: #aab0c0; }
.guc-input:focus { border-color: #2D4B95; }

.guc-send {
  width: 34px; height: 34px; border-radius: 8px;
  background: #2D4B95; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; transition: background .18s, transform .1s;
}
.guc-send:hover:not(:disabled) { background: #1C3A7A; transform: scale(1.05); }
.guc-send:disabled { background: #E0E6F5; cursor: default; }

/* ── Transiciones ── */
.guc-fab-pop-enter-active { animation: guc-pop-in .22s cubic-bezier(.34,1.56,.64,1); }
.guc-fab-pop-leave-active { animation: guc-pop-in .15s ease reverse; }

.guc-win-slide-enter-active { animation: guc-win-in .26s cubic-bezier(.16,1,.3,1); }
.guc-win-slide-leave-active { animation: guc-win-in .18s ease reverse; }

@keyframes guc-pop-in {
  from { transform: scale(.5); opacity: 0; }
  to   { transform: scale(1);  opacity: 1; }
}
@keyframes guc-win-in {
  from { transform: scale(.88) translateY(12px); opacity: 0; }
  to   { transform: scale(1)   translateY(0);    opacity: 1; }
}

/* ── Responsive ── */
@media (max-width: 420px) {
  .guc-fixed { bottom: 12px; right: 12px; }
  .guc-controlled-left { bottom: 12px; left: 12px; }
  .guc-controlled-right { bottom: 12px; right: 12px; }
  .guc-window:not(.guc-window--fullscreen) {
    width: calc(100vw - 24px);
    height: 72vh;
  }
}
</style>